/* eslint-disable no-bitwise */
import type { Fraction } from '../../util/fractionType';
import type { StructuredLogger } from '@mimir/primitive/structuredLogger';
import { invariant } from '../../util/invariant';
import type { BerLength } from './types';
import type { KLV } from './klv';
import { MxfDictionary, SMPTE_UL_INDEX_TABLE_SEGMENT } from './mxfDictionary';
import { MultiArrayBuffer } from './multiArrayBuffer';
import { Parser } from './parser';
import { LocalSetsWriter, parseMxfLocalSets } from './localSets';
import { SizeCalculator } from './sizeCalculator';
import { ArraySerializerTarget, Serializer } from './serializer';

// SMPTE 377 - Table 27 – Index Table Segment Set
export interface DeltaEntry {
  posTableIndex: number, // Int8
  slice: number, // Uint8
  elementDelta: number, // Uint32,
}

// SMPTE 377 - Table 28 – Index Table Segment Set
export const INDEX_ENTRY_FLAG_RANDOM_ACCESS_BIT = (1 << 7);
export const INDEX_ENTRY_FLAG_SEQUENCE_HEADER_BIT = (1 << 6);
export const INDEX_ENTRY_FLAG_FORWARD_PREDICTION_BIT = (1 << 5);
export const INDEX_ENTRY_FLAG_BACKWARD_PREDICTION_BIT = (1 << 4);

export const INDEX_ENTRY_FLAGS_P_FRAME = 0x2;
export const INDEX_ENTRY_FLAGS_B_FRAME = 0x3;

// SMPTE 377 - Table 28 – Index Table Segment Set
export interface IndexEntry {
  temporalOffset: number, // Int8
  keyFrameOffset: number, // Int8
  flags: number, // 8-bits
  streamOffset: number, // Uint64
  sliceOffsets: number[], // Uint32[],
  posTable: Fraction[], // Rational[]
}

// SMPTE 377 - Table 26 – Index Table Segment Set
export interface IndexTableSegment {
  instanceUid: string, // UUID
  indexEditRate: Fraction, // Int32 / Int32
  indexStartPosition: number, // Int64
  indexDuration: number, // Int64
  editUnitByteCount: number, // Uint32
  indexSid: number, // Uint32
  bodySid: number, // Uint32
  sliceCount: number, // Uint8
  posTableCount?: number, // Uint8
  deltaEntries: DeltaEntry[],
  indexEntries: IndexEntry[],
  extStartOffset?: number, // Uint64
  vbeByteCount?:  number, // Uint64
  singleIndexLocation?: boolean, // Boolean, 1 byte
  singleEssenceLocation?: boolean, // Boolean, 1 byte
  forwardIndexDirection?: boolean, // Boolean, 1 byte
}

export function parseIndexTableSegment(
  structuredLogger: StructuredLogger,
  bytes: Uint8Array,
  dictionary: MxfDictionary,
): IndexTableSegment {
  const indexTable : Partial<IndexTableSegment> = {
    deltaEntries: [],
    indexEntries: [],
  };

  const source = new MultiArrayBuffer();
  source.push(bytes);

  const outerParser = new Parser(source);

  parseMxfLocalSets(
    structuredLogger,
    outerParser,
    dictionary,
    (parser, ul, name) => {
      if (name === undefined) {
        throw new Error(`Unknown UL in IndexTableSegment: ${ul}`);
      }
      switch (name) {
        case 'InstanceUID':
          indexTable.instanceUid = parser.readUuid();
          break;
        case 'IndexEditRate':
          indexTable.indexEditRate = parser.readFraction();
          break;
        case 'IndexStartPosition':
          indexTable.indexStartPosition = parser.readUInt64BE();
          break;
        case 'IndexDuration':
          indexTable.indexDuration = parser.readUInt64BE();
          break;
        case 'EditUnitByteCount':
          indexTable.editUnitByteCount = parser.readUInt32BE();
          break;
        case 'IndexSID':
          indexTable.indexSid = parser.readUInt32BE();
          break;
        case 'BodySID':
          indexTable.bodySid = parser.readUInt32BE();
          break;
        case 'SliceCount':
          indexTable.sliceCount = parser.readUInt8();
          break;
        case 'PosTableCount':
          indexTable.posTableCount = parser.readUInt8();
          break;
        case 'DeltaEntryArray':
          indexTable.deltaEntries = parseDeltaEntryArray(parser);
          break;
        case 'IndexEntryArray':
          invariant(indexTable.sliceCount !== undefined, 'Did not expect sliceCount to come after the IndexTableEntries');
          indexTable.indexEntries = parseIndexEntryArray(parser, indexTable.sliceCount, indexTable.posTableCount ?? 0);
          break;
        case 'ExtStartOffset':
          indexTable.extStartOffset = parser.readUInt64BE();
          break;
        case 'VBEByteCount':
          indexTable.vbeByteCount = parser.readUInt64BE();
          break;
        case 'SingleIndexLocation':
          indexTable.singleIndexLocation = Boolean(parser.readUInt8());
          break;
        case 'SingleEssenceLocation':
          indexTable.singleEssenceLocation = Boolean(parser.readUInt8());
          break;
        case 'ForwardIndexDirection':
          indexTable.forwardIndexDirection = Boolean(parser.readUInt8());
          break;
        default:
          throw new Error(`Unhandled item name in index table: ${name}`);
      }
    }
  );

  invariant(indexTable.instanceUid !== undefined, 'IndexTableSegment is missing InstanceUID');
  invariant(indexTable.indexEditRate !== undefined, 'IndexTableSegment is missing "indexEditRate"');
  invariant(indexTable.indexStartPosition !== undefined, 'IndexTableSegment is missing "indexStartPosition"');
  invariant(indexTable.indexDuration !== undefined, 'IndexTableSegment is missing "indexDuration"');
  invariant(indexTable.indexSid !== undefined, 'IndexTableSegment is missing "indexSid"');
  invariant(indexTable.bodySid !== undefined, 'IndexTableSegment is missing "bodySid"');
  invariant(indexTable.deltaEntries !== undefined, 'IndexTableSegment is missing "deltaEntries"');
  invariant(indexTable.indexEntries !== undefined, 'IndexTableSegment is missing "indexEntries"');

  return {
    ...indexTable,
    instanceUid: indexTable.instanceUid,
    indexEditRate: indexTable.indexEditRate,
    indexStartPosition: indexTable.indexStartPosition,
    indexDuration: indexTable.indexDuration,
    indexSid: indexTable.indexSid,
    bodySid: indexTable.bodySid,
    deltaEntries: indexTable.deltaEntries,
    indexEntries: indexTable.indexEntries,

    // The following fields are marked in the standard with Req/D
    // meaning it should have a default value if not present.
    editUnitByteCount: indexTable.editUnitByteCount ?? 0,
    sliceCount: indexTable.sliceCount ?? 0,
  };
}

export function parseIndexTableSegments(structuredLogger: StructuredLogger): TransformStream<KLV, IndexTableSegment> {
  const dictionary = new MxfDictionary();
  return new TransformStream<KLV, IndexTableSegment>({
    transform: (klv: KLV, controller) => {
      if (klv.key === SMPTE_UL_INDEX_TABLE_SEGMENT) {
        controller.enqueue(parseIndexTableSegment(structuredLogger, klv.value, dictionary));
      }
    },
  });
}

// Calculate the payload size of the index table
export function calculateIndexTableSegmentSize(i: IndexTableSegment): number {
  const sizeCalculator = new SizeCalculator();
  const serializer = new Serializer(sizeCalculator);
  const dictionary = new MxfDictionary();
  const localSetsWriter = new LocalSetsWriter(dictionary, serializer);
  serializeIndexTableSegmentInternal(i, localSetsWriter);
  return sizeCalculator.size();
}

// Calculate the total size of the IndexTableSegment KLV
export function calculateIndexTableSegmentKlvSize(i: IndexTableSegment): number {
  return 16 + 4 + calculateIndexTableSegmentSize(i);
}

export function serializeIndexTableSegment(i: IndexTableSegment, dictionary: MxfDictionary): KLV {
  const target = new ArraySerializerTarget();
  const serializer = new Serializer(target);
  const localSetsWriter = new LocalSetsWriter(dictionary, serializer);
  serializeIndexTableSegmentInternal(i, localSetsWriter);
  const value = target.getArray();
  const length : BerLength = { length: value.length, bytesUsed: 4 };

  return { key: SMPTE_UL_INDEX_TABLE_SEGMENT, length, value };
}

function serializeIndexTableSegmentInternal(i: IndexTableSegment, localSetsWriter: LocalSetsWriter) {
  localSetsWriter.writeEntry('InstanceUID', (serializer) => serializer.writeUuid(i.instanceUid));
  localSetsWriter.writeEntry('IndexEditRate', (serializer) => serializer.writeFraction(i.indexEditRate));
  localSetsWriter.writeEntry('IndexStartPosition', (serializer) => serializer.writeUInt64BE(i.indexStartPosition));
  localSetsWriter.writeEntry('IndexDuration', (serializer) => serializer.writeUInt64BE(i.indexDuration));
  localSetsWriter.writeEntry('EditUnitByteCount', (serializer) => serializer.writeUInt32BE(i.editUnitByteCount));
  localSetsWriter.writeEntry('IndexSID', (serializer) => serializer.writeUInt32BE(i.indexSid));
  localSetsWriter.writeEntry('BodySID', (serializer) => serializer.writeUInt32BE(i.bodySid));
  localSetsWriter.writeEntry('SliceCount', (serializer) => serializer.writeUInt8(i.sliceCount));
  if (i.posTableCount !== undefined) {
    // PosTableCount is optional and
    localSetsWriter.writeEntry('PosTableCount', (serializer) => serializer.writeUInt8(i.posTableCount!));
  }
  if (i.extStartOffset !== undefined) {
    localSetsWriter.writeEntry('ExtStartOffset', (serializer) => serializer.writeUInt64BE(i.extStartOffset!));
  }
  if (i.vbeByteCount !== undefined) {
    localSetsWriter.writeEntry('VBEByteCount', (serializer) => serializer.writeUInt64BE(i.vbeByteCount!));
  }

  const singleIndexLocation = i.singleIndexLocation;
  if (singleIndexLocation !== undefined) {
    localSetsWriter.writeEntry('SingleIndexLocation', (serializer) => serializer.writeUInt8FromBoolean(singleIndexLocation));
  }
  const singleEssenceLocation = i.singleEssenceLocation;
  if (singleEssenceLocation !== undefined) {
    localSetsWriter.writeEntry('SingleEssenceLocation', (serializer) => serializer.writeUInt8FromBoolean(singleEssenceLocation));
  }
  const forwardIndexDirection = i.forwardIndexDirection;
  if (forwardIndexDirection !== undefined) {
    localSetsWriter.writeEntry('ForwardIndexDirection', (serializer) => serializer.writeUInt8FromBoolean(forwardIndexDirection));
  }

  localSetsWriter.writeEntry('DeltaEntryArray', (serializer) => writeDeltaEntryArray(serializer, i.deltaEntries));

  localSetsWriter.writeEntry('IndexEntryArray', (serializer) => writeIndexEntryArray(serializer, i.indexEntries, i.sliceCount, i.posTableCount ?? 0));
}

function parseDeltaEntry(parser: Parser): DeltaEntry {
  const posTableIndex : number = parser.readInt8();
  const slice : number = parser.readUInt8();
  const elementDelta: number = parser.readUInt32BE();
  return { posTableIndex, slice, elementDelta };
}

function parseDeltaEntryArray(parser: Parser): DeltaEntry[] {
  const numberOfDeltaEntries = parser.readUInt32BE();
  const expectedDeltaEntryLength = parser.readUInt32BE();

  const deltaEntries : DeltaEntry[] = [];
  for (let i = 0; i < numberOfDeltaEntries; i++) {
    deltaEntries.push(parseDeltaEntry(parser.subParser(expectedDeltaEntryLength)));
  }
  return deltaEntries;
}

function writeDeltaEntry(serializer: Serializer, deltaEntry: DeltaEntry) {
  serializer.writeInt8(deltaEntry.posTableIndex);
  serializer.writeUInt8(deltaEntry.slice);
  serializer.writeUInt32BE(deltaEntry.elementDelta);
}

function writeDeltaEntryArray(serializer: Serializer, deltaEntryArray: DeltaEntry[]) {
  serializer.writeUInt32BE(deltaEntryArray.length);
  serializer.writeUInt32BE(1 + 1 + 4);
  for (const deltaEntry of deltaEntryArray) {
    writeDeltaEntry(serializer, deltaEntry);
  }
}

function parseIndexEntry(
  parser: Parser,
  sliceOffsetCount: number,
  posTableCount: number,
): IndexEntry {
  const temporalOffset: number = parser.readInt8();
  const keyFrameOffset: number = parser.readInt8();
  const flags = parser.readUInt8();
  const streamOffset = parser.readUInt64BE();

  const sliceOffsets : number[] = [];
  for (let i = 0; i < sliceOffsetCount; i++) {
    sliceOffsets.push(parser.readUInt32BE());
  }

  const posTable = [];
  for (let i = 0; i < posTableCount; i++) {
    posTable.push(parser.readFraction());
  }

  return {
    temporalOffset,
    keyFrameOffset,
    flags,
    streamOffset,
    sliceOffsets,
    posTable,
  };
}

function parseIndexEntryArray(
  parser: Parser,
  sliceOffsetCount: number,
  posTableCount: number
): IndexEntry[] {
  const numberOfIndexEntries = parser.readUInt32BE();
  const expectedIndexEntryLength = parser.readUInt32BE();

  const indexEntries = [];
  for (let i = 0; i < numberOfIndexEntries; i++) {
    const indexEntry =
      parseIndexEntry(
        parser.subParser(expectedIndexEntryLength),
        sliceOffsetCount,
        posTableCount
      );

    indexEntries.push(indexEntry);
  }
  return indexEntries;
}

function writeIndexEntry(
  serializer: Serializer,
  indexEntry: IndexEntry,
  sliceOffsetCount: number,
  posTableCount: number,
) {
  serializer.writeInt8(indexEntry.temporalOffset);
  serializer.writeInt8(indexEntry.keyFrameOffset);
  serializer.writeUInt8(indexEntry.flags);
  serializer.writeUInt64BE(indexEntry.streamOffset);
  if (sliceOffsetCount !== indexEntry.sliceOffsets.length) {
    throw new Error(`Incorrect number of slice offsets in index entry: ${indexEntry.sliceOffsets.length}. Index table set to have ${sliceOffsetCount} slice offsets`);
  }
  for (const sliceOffset of indexEntry.sliceOffsets) {
    serializer.writeUInt32BE(sliceOffset);
  }
  if (posTableCount !== indexEntry.posTable.length) {
    throw new Error(`Incorrect number of slice offsets in index entry: ${indexEntry.posTable.length}. Index table set to have ${posTableCount} slice offsets`);
  }
  for (const posTableEntry of indexEntry.posTable) {
    serializer.writeFraction(posTableEntry);
  }
}

function writeIndexEntryArray(
  serializer: Serializer,
  indexEntryArray: IndexEntry[],
  sliceOffsetCount: number,
  posTableCount: number,
) {
  serializer.writeUInt32BE(indexEntryArray.length);
  const elementSize = 1 + 1 + 1 + 8 + (sliceOffsetCount * 4) + (posTableCount * 4);
  serializer.writeUInt32BE(elementSize);
  for (const indexEntry of indexEntryArray) {
    writeIndexEntry(serializer, indexEntry, sliceOffsetCount, posTableCount);
  }
}



